import { z } from 'zod';

/**
 * The contract between Cairn's page and the desktop application around it.
 *
 * The page is the same web client the browser runs, bundled into the app and
 * served from `app://cairn`. Its preload exposes the calls in
 * `desktop-bridge.ts` and nothing else; main validates every message the page
 * sends with these schemas and the page validates every command main sends the
 * same way, so neither side trusts the other's shapes. Nothing here imports
 * Electron or the DOM. See docs/desktop-app.md.
 */

/** How far ahead the page tells main about reminders. */
export const reminderHorizonMs = 48 * 60 * 60 * 1000;

/** Main schedules at most this many reminders, nearest first. */
export const reminderLimit = 64;

const text = (max: number) => z.string().max(max);

/** The owner's appearance choice, as the page stores it under `cairn-theme`. */
export const appearanceSchema = z.enum(['system', 'light', 'dark']);

export type Appearance = z.infer<typeof appearanceSchema>;

export const desktopReminderSchema = z.object({
  key: text(300),
  recordId: z.string().uuid(),
  channel: z.enum(['appointment', 'birthday']),
  fireAt: z.iso.datetime(),
  title: text(500),
  body: text(1000),
});

export type DesktopReminder = z.infer<typeof desktopReminderSchema>;

/**
 * What the page publishes whenever it changes. Reminders carry an event's
 * title and time, as a notification must; nothing from the vault is ever part
 * of this state.
 */
export const desktopStateSchema = z.object({
  signedIn: z.boolean(),
  appearance: appearanceSchema,
  dark: z.boolean(),
  focus: z
    .object({
      taskId: z.string().uuid(),
      title: text(500),
      /** When the running interval began, or null on a break. */
      runningSince: z.number().int().nonnegative().nullable(),
      /** Closed intervals so far, before the running one. */
      elapsedMs: z.number().int().nonnegative(),
    })
    .nullable(),
  today: z.object({
    placed: z.number().int().min(0).max(1000),
    total: z.number().int().min(0).max(1000),
  }),
  reminders: z.array(desktopReminderSchema).max(reminderLimit),
});

export type DesktopState = z.infer<typeof desktopStateSchema>;

/** Where the auto-updater stands, as the About box and the update notice show it. */
export const updateStatusSchema = z.discriminatedUnion('state', [
  /** Not updating at all, with the reason the About box gives. */
  z.object({ state: z.literal('off'), reason: text(300) }),
  z.object({ state: z.literal('idle') }),
  z.object({ state: z.literal('checking') }),
  z.object({ state: z.literal('current'), checkedAt: z.number().int() }),
  z.object({
    state: z.literal('downloading'),
    version: text(64),
    percent: z.number().min(0).max(100),
  }),
  z.object({ state: z.literal('ready'), version: text(64) }),
  z.object({ state: z.literal('failed'), message: text(500) }),
]);

export type UpdateStatus = z.infer<typeof updateStatusSchema>;

export const updateActionSchema = z.enum(['status', 'check', 'restart']);

export type UpdateAction = z.infer<typeof updateActionSchema>;

/** A path inside the app the page may be asked to open: its own routes, nothing else. */
const appPath = z
  .string()
  .max(200)
  .regex(/^\/[A-Za-z0-9/_-]*$/);

export const captureEntrySchema = z.object({
  title: z.string().trim().min(1).max(500),
  /** Choose it for today rather than leave it in the Inbox. */
  today: z.boolean(),
  /** Open the task in the window after saving. */
  open: z.boolean(),
});

export type CaptureEntry = z.infer<typeof captureEntrySchema>;

export const desktopCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('navigate'), path: appPath }),
  z.object({ type: z.literal('palette') }),
  z.object({ type: z.literal('about') }),
  z.object({ type: z.literal('new-note') }),
  z.object({ type: z.literal('set-out') }),
  z.object({ type: z.literal('sync') }),
  z.object({ type: z.literal('appearance'), value: appearanceSchema }),
  z.object({ type: z.literal('full-screen'), value: z.boolean() }),
  z.object({ type: z.literal('updates'), status: updateStatusSchema }),
  z.object({ type: z.literal('capture'), id: z.string().uuid(), entry: captureEntrySchema }),
]);

export type DesktopCommand = z.infer<typeof desktopCommandSchema>;

export const desktopReplySchema = z.object({
  id: z.string().uuid(),
  ok: z.boolean(),
  error: text(300).optional(),
});

export type DesktopReply = z.infer<typeof desktopReplySchema>;

export type CaptureResult = { ok: true } | { ok: false; error: string };

export type AboutInfo = {
  name: string;
  version: string;
  server: string;
  arch: string;
  os: string;
  versions: { electron: string; chrome: string; node: string };
};
